import React, { useState, useEffect, useCallback } from 'react';
import { getActivityLog, getActivityFilterOptions } from '../services/adminActivityService';
import TimeRangeSelector from '../components/TimeRangeSelector';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { ADMIN_ACTIVITY_LOG_TABLE_ID } from '../lib/appwrite';
import './ActivityLogPage.css';

const formatDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const ActivityLogPage = () => {
  const [activities, setActivities] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterAdmin, setFilterAdmin] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterResource, setFilterResource] = useState('');
  const [timeRange, setTimeRange] = useState('all');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [logRes, optionsRes] = await Promise.all([
        getActivityLog({
          adminUserId: filterAdmin || undefined,
          action: filterAction || undefined,
          resourceType: filterResource || undefined,
          timeRange,
          limit: 500,
        }),
        getActivityFilterOptions(),
      ]);
      setActivities(logRes || []);
      setFilterOptions(optionsRes);
    } catch (err) {
      const msg = err.message || 'Failed to load activity log';
      const isAuthError =
        typeof msg === 'string' &&
        (msg.toLowerCase().includes('not authorized') || msg.toLowerCase().includes('unauthorized'));
      setError(
        isAuthError
          ? `${msg} Add REACT_APP_APPWRITE_API_KEY to .env for server-level access.`
          : msg
      );
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [filterAdmin, filterAction, filterResource, timeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [ADMIN_ACTIVITY_LOG_TABLE_ID] });

  if (loading && !filterOptions) {
    return (
      <div className="activity-log-loading">
        <div className="dashboard-spinner" />
        <p>Loading activity log...</p>
      </div>
    );
  }

  return (
    <div className="activity-log-page">
      <div className="activity-log-header">
        <div>
          <h1 className="dashboard-title">Activity Log</h1>
          <p className="dashboard-subtitle">
            Monitor all admin actions across the platform.
          </p>
        </div>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
      </div>

      {error && (
        <div className="activity-log-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="activity-log-toolbar">
        <select
          value={filterAdmin}
          onChange={(e) => setFilterAdmin(e.target.value)}
          className="activity-log-filter"
        >
          <option value="">All admins</option>
          {(filterOptions?.admins || []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="activity-log-filter"
        >
          <option value="">All actions</option>
          {(filterOptions?.actions || []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={filterResource}
          onChange={(e) => setFilterResource(e.target.value)}
          className="activity-log-filter"
        >
          <option value="">All resources</option>
          {(filterOptions?.resourceTypes || []).map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button type="button" className="activity-log-btn-refresh" onClick={loadData}>
          ⟳ Refresh
        </button>
      </div>

      <div className="activity-log-table-wrap">
        <table className="activity-log-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Admin</th>
              <th>Action</th>
              <th>Resource</th>
              <th>Resource ID</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {activities.length === 0 ? (
              <tr>
                <td colSpan={6} className="activity-log-empty">
                  <p>No activity found.</p>
                  <p className="activity-log-empty-hint">
                    Activity is logged when you create, update, or delete stores, products, users, admin users, or log in.
                    Try selecting &quot;All time&quot; for the time range.
                  </p>
                </td>
              </tr>
            ) : (
              activities.map((a) => (
                <tr key={a.$id}>
                  <td>{formatDateTime(a.createdAt)}</td>
                  <td>
                    <span className="activity-log-admin">{a.adminUserName || a.adminUserEmail || a.adminUserId}</span>
                  </td>
                  <td>
                    <span className={`activity-log-action activity-log-action-${(a.action || '').toLowerCase()}`}>
                      {a.action || '—'}
                    </span>
                  </td>
                  <td>{a.resourceType || '—'}</td>
                  <td className="activity-log-mono">{a.resourceId ? String(a.resourceId).substring(0, 20) + (a.resourceId.length > 20 ? '…' : '') : '—'}</td>
                  <td className="activity-log-details">{a.details || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivityLogPage;
